import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import GymMemberFields from './GymMemberFields';
import { emptyGymMemberForm, toGymMemberForm } from './gym-member-form';
import { gymMembersService } from '../../services/gymMembers';
import type { GymMember, GymMemberPayload } from '../../types/gym-member';

interface GymMemberFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  /** Null when creating. */
  member: GymMember | null;
  /** Receives the saved member; it may come back already linked to an account. */
  onSaved: (member: GymMember) => void;
}

/**
 * Create/edit form for a gym's own membership record, shown from the roster.
 * The standalone edit page uses the same fields via GymMemberFields.
 */
export default function GymMemberFormDialog({
  open,
  onOpenChange,
  gymSlug,
  member,
  onSaved,
}: GymMemberFormDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<GymMemberPayload>(emptyGymMemberForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setForm(member ? toGymMemberForm(member) : emptyGymMemberForm);
  }, [open, member]);

  // Surname is optional on the record, but asked for on manual entry: staff
  // typing someone in at the desk have it, unlike a prefilled QR registration.
  const canSave = form.name.trim().length > 0 && (form.surname ?? '').trim().length > 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave || saving) return;

    setSaving(true);
    try {
      const saved = member
        ? await gymMembersService.update(gymSlug, member.id, form)
        : await gymMembersService.create(gymSlug, form);

      onSaved(saved);
    } catch (error) {
      toast({
        title: member ? t('gymAdmin.members.updateError') : t('gymAdmin.members.createError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {member ? t('gymAdmin.members.editTitle') : t('gymAdmin.members.newTitle')}
          </DialogTitle>
          <DialogDescription>{t('gymAdmin.members.formDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <GymMemberFields form={form} onChange={setForm} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave || saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
